import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Select,
  MenuItem,
  Typography,
  Paper,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Switch,
} from '@mui/material';
import {
  X as CloseIcon,
  Settings as SettingsIcon,
  Trash2 as DeleteIcon,
  Cloud as CloudQueueIcon,
  Database as StorageIcon,
  Clock as AccessTimeIcon,
  Code as CodeIcon,
  GitBranch as AccountTreeIcon,
  Tag as TagIcon,
  Wifi as WifiIcon,
  Cable as SettingsInputComponentIcon,
  Plus as AddIcon,
  Trash as DeleteOutlineIcon,
} from 'lucide-react';
import api from './api';
import { toast } from 'react-toastify';

const TaskSettingsDialog = ({
  open,
  onClose,
  onSave,
  onDelete,
  onOpenContent,  // Callback to open workflow/code editor
  initialSettings = null,
  isNew = false
}) => {
  const defaultSettings = {
    taskName: '',
    taskType: 'workflow',
    executionLocation: 'local',
    executionMode: 'scheduler',
    schedulerSettings: {
      type: 'interval',
      interval: 3600,
      customCron: '',
      specificTimes: ['00:00'],
    },
    socketTriggerSettings: {
      socketId: 0,
      triggerEvent: 'data_received',
      messageFilter: '',
      dataFormat: 'text',
      parseData: false,
      dataMappings: {},
      socketName: ''
    },
    webSocketTriggerSettings: {
      webSocketId: 0,
      triggerEvent: 'message_received',
      messageType: '',
      messageFilter: '',
      parseMessage: false,
      messageMappings: {},
      webSocketName: ''
    },
    timeout: 3,
    isEnabled: true,
    enableLogging: false,
  };

  const [workflowList, setWorkflowList] = useState([]);
  const [codeList, setCodeList] = useState([]);
  const [selectedWorkflowId, setSelectedWorkflowId] = useState('');
  const [selectedCodeId, setSelectedCodeId] = useState('');
  const [tags, setTags] = useState([]);
  const [tagCondition, setTagCondition] = useState(null);
  const [sockets] = useState([]);
  const [webSockets] = useState([]);

  const defaultCodeSettings = {
    language: 'python'
  };

  const [settings, setSettings] = useState(defaultSettings);
  const [codeSettings, setCodeSettings] = useState(defaultCodeSettings);

  const getOperatorIcon = (operator) => {
    const operatorIcons = {
      'equals': '=',
      'notEquals': '≠',
      'greaterThan': '>',
      'lessThan': '<',
      'greaterThanOrEqual': '≥',
      'lessThanOrEqual': '≤',
      'risingEdge': '↗',
      'fallingEdge': '↘'
    };
    return operatorIcons[operator] || '=';
  };

  const mapInitialSettings = (data) => {
    if (!data) return defaultSettings;

    console.log('Initial data for mapping:', data);

    // Parse configuration safely
    let config = {};
    try {
      if (data.configuration) {
        config = typeof data.configuration === 'string'
          ? JSON.parse(data.configuration)
          : data.configuration;
      }
    } catch (error) {
      console.warn('Configuration parsing error:', error);
      config = {};
    }

    console.log('Parsed configuration:', config);

    // Pre-process task type and name
    let taskType = (data.taskType || data.Type || 'workflow').toLowerCase();
    // Normalize 'python' and 'csharp' to 'code'
    if (taskType === 'python' || taskType === 'csharp') {
      taskType = 'code';
    }
    const taskName = data.taskName || data.name || '';
    const contentName = data.contentName || '';

    // Handle execution settings
    const executionLocation = (data.executionLocation || config.ExecutionLocation || 'local').toLowerCase();
    const executionMode = (data.executionMode || config.ExecutionMode || 'scheduler').toLowerCase();

    // Initialize tag-based settings
    let tagBasedSettings = {
      tagId: '',
      triggerCondition: 'change',
      comparisonOperator: 'equals',
      thresholdValue: ''
    };

    // Process TagBasedTasks if available
    if (executionMode === 'tag-based' && config.TagBasedTasks?.length > 0) {
      const tagConfig = config.TagBasedTasks[0];
      if (tagConfig) {
        tagBasedSettings = {
          tagId: `tag_${tagConfig.TagId}`,
          triggerCondition: tagConfig.TriggerCondition || 'change',
          comparisonOperator: tagConfig.ComparisonOperator || 'equals',
          thresholdValue: tagConfig.ThresholdValue?.toString() || ''
        };
      }
    }

    // Initialize scheduler settings
    const schedulerSettings = {
      type: config.SchedulerSettings?.type || 'interval',
      interval: Number(config.SchedulerSettings?.interval || 3600),
      customCron: config.SchedulerSettings?.customCron || '',
      specificTimes: Array.isArray(config.SchedulerSettings?.specificTimes) ?
        config.SchedulerSettings.specificTimes : ['00:00']
    };

    console.log('Parsed scheduler settings:', schedulerSettings);

    // Map everything to final settings object
    const mappedSettings = {
      taskName: taskName,
      taskType: taskType,
      executionLocation: executionLocation,
      executionMode: executionMode,
      schedulerSettings: schedulerSettings,
      timeout: Number(config.Timeout || config.WatchdogTimeout || 3000) / 1000,
      isEnabled: data.isEnabled ?? true,
      contentName: contentName,
      enableLogging: config.EnableLogging ?? false,
    };

    // Add task-specific settings if needed
    if (executionMode === 'tag-based') {
      mappedSettings.tagBasedSettings = tagBasedSettings;
    }

    if (taskType === 'code') {
      mappedSettings.language = config.Language || 'python';
    }

    console.log('Final mapped settings:', mappedSettings);
    return mappedSettings;
  };

  const [tagBasedSettings, setTagBasedSettings] = useState({
    tagId: '',
    triggerCondition: 'change',
    comparisonOperator: 'greaterThan',
    thresholdValue: 50
  });

  const comparisonOperators = [
    { value: "greaterThan", label: "Greater Than", icon: ">" },
    { value: "lessThan", label: "Less Than", icon: "<" },
    { value: "equals", label: "Equals", icon: "=" }
  ];

  const triggerConditions = [
    { value: "risingEdge", label: "Rising Edge (0->1)" },
    { value: "fallingEdge", label: "Falling Edge (1->0)" },
    { value: "change", label: "Value Change" }
  ];

  const handleAddTime = () => {
    setSettings(prev => ({
      ...prev,
      schedulerSettings: {
        ...prev.schedulerSettings,
        specificTimes: [...prev.schedulerSettings.specificTimes, '00:00']
      }
    }));
  };

  const renderConditionDisplay = (condition) => {
    if (!condition) return 'No condition selected';

    const var1Name = condition.variable1?.variableName || '';
    const operatorIcon = condition.operatorIcon || '';
    const var2Name = condition.variable2?.variableName || '';

    return `${var1Name} ${operatorIcon} ${var2Name}`;
  };

  const handleRemoveTime = (index) => {
    setSettings(prev => ({
      ...prev,
      schedulerSettings: {
        ...prev.schedulerSettings,
        specificTimes: prev.schedulerSettings.specificTimes.filter((_, i) => i !== index)
      }
    }));
  };

  const handleTimeChange = (index, newValue) => {
    setSettings(prev => ({
      ...prev,
      schedulerSettings: {
        ...prev.schedulerSettings,
        specificTimes: prev.schedulerSettings.specificTimes.map((time, i) =>
          i === index ? newValue : time
        )
      }
    }));
  };


useEffect(() => {
  if (!open) return;

  const loadInitialData = async () => {
    try {
      // Load data separately to handle individual failures
      let workflows = [];
      let codes = [];
      let variables = [];

      try {
        workflows = await api.listWorkflows() || [];
      } catch (e) {
        console.error('Error loading workflows:', e);
      }

      try {
        codes = await api.getCodes() || [];
        console.log('Loaded codes:', codes);
      } catch (e) {
        console.error('Error loading codes:', e);
      }

      try {
        variables = await api.getVariables() || [];
      } catch (e) {
        console.error('Error loading variables:', e);
      }

      const tagsList = variables.filter(v => v.type === 'T');

      setWorkflowList(workflows);
      setCodeList(codes);
      setTags(tagsList);

      console.log('Data loaded - workflows:', workflows.length, 'codes:', codes.length, 'tags:', tagsList.length);

      // After initial load, set initial settings
      if (initialSettings) {
        console.log('Loading initial settings:', initialSettings);

        // Parse the data
        const mappedSettings = mapInitialSettings(initialSettings);
        setSettings(mappedSettings);

        // Parse configuration for case-sensitivity
        const config = typeof initialSettings.configuration === 'string'
          ? JSON.parse(initialSettings.configuration)
          : initialSettings.configuration;

        // Prepare tagCondition for tag-based task
        if (initialSettings.executionMode === 'tag-based' && config.TagBasedTasks?.length > 0) {
          const tagConfig = config.TagBasedTasks[0];
          if (tagConfig) {
            // Find matching tag from tag list
            const matchingTag = tagsList.find(t => Number(t.tagID) === Number(tagConfig.TagId));
            const tagName = matchingTag ? matchingTag.name : `Tag-${tagConfig.TagId}`;

            // Create TagCondition object
            setTagCondition({
              variable1: {
                variableId: `tag_${tagConfig.TagId}`,
                variableName: tagName,
                variableType: tagConfig.TagType || 'T',
                tagID: tagConfig.TagId
              },
              operator: tagConfig.ComparisonOperator || 'equals',
              operatorIcon: getOperatorIcon(tagConfig.ComparisonOperator || 'equals'),
              variable2: {
                variableId: tagConfig.ThresholdValue?.toString() || '',
                variableName: tagConfig.ThresholdValue?.toString() || '',
                variableType: 'constant'
              }
            });

            // Update TagBasedSettings
            setTagBasedSettings({
              tagId: `tag_${tagConfig.TagId}`,
              triggerCondition: tagConfig.TriggerCondition || 'change',
              comparisonOperator: tagConfig.ComparisonOperator || 'equals',
              thresholdValue: tagConfig.ThresholdValue?.toString() || ''
            });
          }
        }

        // For Code or Workflow selection
        if (mappedSettings.taskType === 'workflow' && mappedSettings.contentName) {
          const workflow = workflows.find(w => w.name === mappedSettings.contentName);
          if (workflow) {
            console.log('Selected workflow:', workflow);
            setSelectedWorkflowId(workflow.id);
          }
        }

        // Update special settings for code
        if (mappedSettings.taskType === 'code' || mappedSettings.taskType === 'python') {
          setCodeSettings({
            language: config.Language || 'python'
          });
          // Find and select the code by contentName
          if (mappedSettings.contentName && codes) {
            const code = codes.find(c => c.name === mappedSettings.contentName);
            if (code) {
              console.log('Selected code:', code);
              setSelectedCodeId(code.id);
            }
          }
        }
      }
    } catch (error) {
      console.error('Error loading initial data:', error);
      toast.error('Veri yüklenirken hata oluştu');
    }
  };

  loadInitialData();
}, [open, initialSettings]);

  useEffect(() => {
    if (settings.tagBasedSettings && settings.executionMode === 'tag-based') {
        setTagBasedSettings(settings.tagBasedSettings);
    }
  }, [settings.tagBasedSettings, settings.executionMode]);

  useEffect(() => {
    if (selectedWorkflowId && workflowList.length > 0) {
        const selectedWorkflow = workflowList.find(w => w.id === selectedWorkflowId);
        if (selectedWorkflow) {
            setSettings(prev => ({
                ...prev,
                contentName: selectedWorkflow.name
            }));
        }
    }
  }, [selectedWorkflowId, workflowList]);

  useEffect(() => {
    if (selectedCodeId && codeList.length > 0) {
        const selectedCode = codeList.find(c => c.id === selectedCodeId);
        if (selectedCode) {
            setSettings(prev => ({
                ...prev,
                contentName: selectedCode.name
            }));
        }
    }
  }, [selectedCodeId, codeList]);

  const schedulerTypes = [
    { value: 'interval', label: 'Aralık' },
    { value: 'specific', label: 'Günde Belirli Saatlerde' }
    // CRON seçeneği kaldırıldı - scheduler ile uyumsuzluk nedeniyle
  ];

  const cronExamples = [
    { pattern: '* * * * *', description: 'Her dakika' },
    { pattern: '0 * * * *', description: 'Her saat' },
    { pattern: '0 0 * * *', description: 'Her gün gece yarısı' },
    { pattern: '*/5 * * * *', description: 'Her 5 dakikada bir' },
    { pattern: '0 9 * * 1-5', description: 'Hafta içi her gün 9:00' },
    { pattern: '0 0 1 * *', description: 'Ayın ilk günü gece yarısı' }
];

  const intervalPresets = [
    { value: 20, label: '20 saniye' },
    { value: 60, label: '1 dakika' },
    { value: 300, label: '5 dakika' },
    { value: 900, label: '15 dakika' },
    { value: 1800, label: '30 dakika' },
    { value: 3600, label: '1 saat' },
    { value: 86400, label: '24 saat' },
  ];

  const programmingLanguages = [
    { value: 'python', label: 'Python' },
    { value: 'csharp', label: 'C#' }
  ];

  const handleChange = (field, subfield = null) => (event) => {
    if (subfield) {
      setSettings(prev => ({
        ...prev,
        [field]: {
          ...prev[field],
          [subfield]: event.target.value
        }
      }));
    } else {
      setSettings(prev => ({
        ...prev,
        [field]: event.target.value
      }));
    }
  };

  const handleCodeSettingChange = (field) => (event) => {
    setCodeSettings(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleSwitchChange = (field) => (event) => {
    setSettings(prev => ({
      ...prev,
      [field]: event.target.checked
    }));
  };

  const createTagBasedConfig = (tagCondition) => {
    if (!tagCondition?.variable1?.variableId) {
      console.warn("Invalid variable1");
      return null;
    }

    // Parse the variable ID to extract tag ID
    const tagIdMatch = tagCondition.variable1.variableId.match(/^(tag|var)_(\d+)$/);
    if (!tagIdMatch) {
      console.warn("Invalid variable ID format");
      return null;
    }

    const tagId = parseInt(tagIdMatch[2]);
    const tagType = tagIdMatch[1] === 'tag' ? 'T' : 'V';

    return {
      TagId: tagId,
      TagType: tagType,
      TriggerCondition: tagCondition.operator || 'change',
      ComparisonOperator: tagCondition.operator || 'equals',
      ThresholdValue: parseFloat(tagCondition.variable2?.variableName) || 0
    };
  };

  const handleSave = async () => {
    if (!settings.taskName) {
      toast.error('Görev adı boş olamaz!');
      return;
    }

    try {
      console.log("Saving task with settings:", settings);
      console.log("Current tag condition:", tagCondition);

      let TagBasedTasks = [];
      let UsedTags = [];

      // Prepare configuration for tag-based task
      if (settings.executionMode === 'tag-based' && tagCondition) {
        // Get tag ID from variable1
        if (tagCondition.variable1?.variableId) {
          const tagMatch = tagCondition.variable1.variableId.match(/^(tag|var)_(\d+)$/);
          if (tagMatch) {
            const tagId = parseInt(tagMatch[2]);
            const tagType = tagMatch[1] === 'tag' ? 'T' : 'V';

            // Create TagBasedTask item
            const tagConfig = {
              TagId: tagId,
              TagType: tagType,
              TriggerCondition: tagCondition.operator || 'change',
              ComparisonOperator: tagCondition.operator || 'equals',
              ThresholdValue: parseFloat(tagCondition.variable2?.variableName) || 0
            };

            console.log("Created tag config:", tagConfig);
            TagBasedTasks.push(tagConfig);
            UsedTags.push(tagId.toString());
          }
        } else if (tagCondition.variable1?.tagID) {
          // Alternatively use direct tagID
          const tagId = tagCondition.variable1.tagID;
          const tagType = tagCondition.variable1.variableType || 'T';

          const tagConfig = {
            TagId: tagId,
            TagType: tagType,
            TriggerCondition: tagCondition.operator || 'change',
            ComparisonOperator: tagCondition.operator || 'equals',
            ThresholdValue: parseFloat(tagCondition.variable2?.variableName) || 0
          };

          console.log("Created tag config from tagID:", tagConfig);
          TagBasedTasks.push(tagConfig);
          UsedTags.push(tagId.toString());
        }
      }

      // Prepare task request
      const taskRequest = {
        Name: settings.taskName,
        Type: settings.taskType,
        ExecutionLocation: settings.executionLocation,
        ExecutionMode: settings.executionMode,
        IsEnabled: settings.isEnabled,
        ContentName: settings.contentName,
        Configuration: {
          ExecutionLocation: settings.executionLocation,
          ExecutionMode: settings.executionMode,
          Language: settings.taskType === 'code' ? (codeSettings.language || 'python') : null,
          WorkflowName: settings.taskType === 'workflow' ? settings.contentName : "",
          CodeName: settings.taskType === 'code' ? settings.contentName : "",
          EnableLogging: settings.enableLogging,
          WatchdogTimeout: settings.timeout * 1000,
          Timeout: settings.timeout * 1000,
          TagBasedTasks: TagBasedTasks,
          UsedTags: UsedTags,
          SchedulerSettings: settings.executionMode === 'scheduler' ? {
            type: settings.schedulerSettings?.type || 'interval',
            interval: Number(settings.schedulerSettings?.interval || 3600),
            customCron: settings.schedulerSettings?.customCron || '',
            specificTimes: Array.isArray(settings.schedulerSettings?.specificTimes) ?
              settings.schedulerSettings.specificTimes : ['00:00'],
            specificTime: null
          } : null,
          SocketTriggerSettings: settings.executionMode === 'socket-trigger' ? {
            socketId: settings.socketTriggerSettings?.socketId || 0,
            triggerEvent: settings.socketTriggerSettings?.triggerEvent || 'data_received',
            messageFilter: settings.socketTriggerSettings?.messageFilter || '',
            dataFormat: settings.socketTriggerSettings?.dataFormat || 'text',
            parseData: settings.socketTriggerSettings?.parseData || false,
            dataMappings: settings.socketTriggerSettings?.dataMappings || {},
            socketName: settings.socketTriggerSettings?.socketName || ''
          } : null,
          WebSocketTriggerSettings: settings.executionMode === 'websocket-trigger' ? {
            webSocketId: settings.webSocketTriggerSettings?.webSocketId || 0,
            triggerEvent: settings.webSocketTriggerSettings?.triggerEvent || 'message_received',
            messageType: settings.webSocketTriggerSettings?.messageType || '',
            messageFilter: settings.webSocketTriggerSettings?.messageFilter || '',
            parseMessage: settings.webSocketTriggerSettings?.parseMessage || false,
            messageMappings: settings.webSocketTriggerSettings?.messageMappings || {},
            webSocketName: settings.webSocketTriggerSettings?.webSocketName || ''
          } : null
        }
      };

      console.log("Final task request:", taskRequest);

      if (onSave) {
        await onSave(settings.taskName, taskRequest);
      }

      onClose();

    } catch (error) {
      console.error('Save error:', error);
      const errorMessage = error.response?.data?.message ||
        error.response?.data?.details ||
        'Görev kaydedilirken hata oluştu!';
      toast.error(errorMessage);
    }
  };

    const handleClose = () => {
        onClose();

        setTimeout(() => {
        setSettings(defaultSettings);
        setCodeSettings(defaultCodeSettings);
        }, 0);
    };

    const handleDelete = async () => {
      try {
        if (window.confirm('Bu görevi silmek istediğinizden emin misiniz?')) {
          await api.deleteTask(settings.taskName);
          onClose();
          toast.success('Görev başarıyla silindi');
        }
      } catch (error) {
        toast.error('Görev silinirken hata oluştu');
      }
    };

    const renderTagBasedSettings = () => (
      <>
        {settings.executionMode === 'tag-based' && (
          <>
            <TableRow>
              <TableCell>Tag Seçimi</TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexDirection: 'column' }}>
                  <TextField
                    fullWidth
                    size="small"
                    value={tagCondition?.variable1?.variableName || ''}
                    placeholder="Tag seçin..."
                    InputProps={{
                      readOnly: true,
                    }}
                  />
                  <FormControl fullWidth size="small">
                    <Select
                      value={tagCondition?.variable1?.variableId || ''}
                      onChange={(e) => {
                        const selectedTag = tags.find(t => t.id === e.target.value);
                        if (selectedTag) {
                          setTagCondition(prev => ({
                            ...prev,
                            variable1: {
                              variableId: selectedTag.id,
                              variableName: selectedTag.name,
                              variableType: 'T',
                              tagID: selectedTag.tagID
                            }
                          }));
                        }
                      }}
                      displayEmpty
                    >
                      <MenuItem value="">
                        <em>Tag seçin...</em>
                      </MenuItem>
                      {tags.map(tag => (
                        <MenuItem key={tag.id} value={tag.id}>
                          {tag.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Karşılaştırma Operatörü</TableCell>
              <TableCell>
                <FormControl fullWidth size="small">
                  <Select
                    value={tagCondition?.operator || 'equals'}
                    onChange={(e) => {
                      setTagCondition(prev => ({
                        ...prev,
                        operator: e.target.value,
                        operatorIcon: getOperatorIcon(e.target.value)
                      }));
                    }}
                  >
                    <MenuItem value="equals">=</MenuItem>
                    <MenuItem value="notEquals">≠</MenuItem>
                    <MenuItem value="greaterThan">&gt;</MenuItem>
                    <MenuItem value="lessThan">&lt;</MenuItem>
                    <MenuItem value="greaterThanOrEqual">≥</MenuItem>
                    <MenuItem value="lessThanOrEqual">≤</MenuItem>
                  </Select>
                </FormControl>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Karşılaştırma Değeri</TableCell>
              <TableCell>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  value={tagCondition?.variable2?.variableName || ''}
                  onChange={(e) => {
                    setTagCondition(prev => ({
                      ...prev,
                      variable2: {
                        variableId: e.target.value,
                        variableName: e.target.value,
                        variableType: 'constant'
                      }
                    }));
                  }}
                  placeholder="Değer girin..."
                />
              </TableCell>
            </TableRow>
          </>
        )}
      </>
    );

    const renderWorkflowCodeSelection = () => (
      <>
          <TableRow>
              <TableCell colSpan={2}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: '#6a1b9a',
                      fontWeight: 600
                    }}
                  >
                      {settings.taskType === 'workflow' ? 'Workflow Seçimi' : 'Kod Seçimi'}
                  </Typography>
              </TableCell>
          </TableRow>
          <TableRow>
              <TableCell>Ad</TableCell>
              <TableCell>
                  <FormControl fullWidth size="small">
                      <Select
                          value={settings.taskType === 'workflow' ? selectedWorkflowId : selectedCodeId}
                          onChange={(e) => {
                              if (settings.taskType === 'workflow') {
                                  setSelectedWorkflowId(e.target.value);
                                  const selectedWorkflow = workflowList.find(w => w.id === e.target.value);
                                  if (selectedWorkflow) {
                                      setSettings(prev => ({
                                          ...prev,
                                          contentName: selectedWorkflow.name
                                      }));
                                  }
                              } else {
                                  setSelectedCodeId(e.target.value);
                                  const selectedCode = codeList.find(c => c.id === e.target.value);
                                  if (selectedCode) {
                                      setSettings(prev => ({
                                          ...prev,
                                          contentName: selectedCode.name
                                      }));
                                  }
                              }
                          }}
                      >
                          {(settings.taskType === 'workflow' ? workflowList : codeList).map(item => (
                              <MenuItem key={item.id} value={item.id}>
                                  {item.name} {item.description && `(${item.description})`}
                              </MenuItem>
                          ))}
                      </Select>
                  </FormControl>
              </TableCell>
          </TableRow>
          {/* Open Content Button - Opens workflow or code editor */}
          {settings.contentName && (
            <TableRow>
              <TableCell>İçerik</TableCell>
              <TableCell>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={settings.taskType === 'workflow' ? <AccountTreeIcon size={16} /> : <CodeIcon size={16} />}
                  onClick={() => {
                    if (onOpenContent && settings.contentName) {
                      onOpenContent(settings.taskType, settings.contentName);
                    }
                  }}
                  sx={{
                    borderColor: '#6a1b9a',
                    color: '#6a1b9a',
                    '&:hover': {
                      borderColor: '#4a0e6a',
                      backgroundColor: 'rgba(106, 27, 154, 0.04)'
                    }
                  }}
                >
                  {settings.taskType === 'workflow' ? 'Workflow Editörde Aç' : 'Kod Editörde Aç'}
                </Button>
              </TableCell>
            </TableRow>
          )}
      </>
    );

    const renderSocketTriggerSettings = () => (
      <>
        {settings.executionMode === 'socket-trigger' && (
          <>
            <TableRow>
              <TableCell colSpan={2}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: '#6a1b9a',
                    fontWeight: 600,
                    mt: 2
                  }}
                >
                  Socket Tetikleyici Ayarları
                </Typography>
              </TableCell>
            </TableRow>

            <TableRow>
              <TableCell>Socket Bağlantısı</TableCell>
              <TableCell>
                <FormControl fullWidth size="small">
                  <Select
                    value={settings.socketTriggerSettings.socketId}
                    onChange={handleChange('socketTriggerSettings', 'socketId')}
                    displayEmpty
                  >
                    <MenuItem value={0}>Socket seçin...</MenuItem>
                    {(sockets || []).map(socket => (
                      <MenuItem key={socket.socketId} value={socket.socketId}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <SettingsInputComponentIcon size={16} />
                          {socket.socketName} ({socket.host}:{socket.port})
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </TableCell>
            </TableRow>

            <TableRow>
              <TableCell>Tetikleme Olayı</TableCell>
              <TableCell>
                <FormControl fullWidth size="small">
                  <Select
                    value={settings.socketTriggerSettings.triggerEvent}
                    onChange={handleChange('socketTriggerSettings', 'triggerEvent')}
                  >
                    <MenuItem value="data_received">Veri Alındı</MenuItem>
                    <MenuItem value="data_sent">Veri Gönderildi</MenuItem>
                    <MenuItem value="connection_change">Bağlantı Değişti</MenuItem>
                  </Select>
                </FormControl>
              </TableCell>
            </TableRow>

            <TableRow>
              <TableCell>Veri Formatı</TableCell>
              <TableCell>
                <FormControl fullWidth size="small">
                  <Select
                    value={settings.socketTriggerSettings.dataFormat}
                    onChange={handleChange('socketTriggerSettings', 'dataFormat')}
                  >
                    <MenuItem value="text">Metin</MenuItem>
                    <MenuItem value="json">JSON</MenuItem>
                    <MenuItem value="xml">XML</MenuItem>
                    <MenuItem value="binary">Binary</MenuItem>
                  </Select>
                </FormControl>
              </TableCell>
            </TableRow>

            <TableRow>
              <TableCell>Mesaj Filtresi</TableCell>
              <TableCell>
                <TextField
                  fullWidth
                  size="small"
                  value={settings.socketTriggerSettings.messageFilter}
                  onChange={handleChange('socketTriggerSettings', 'messageFilter')}
                  placeholder="Regex deseni (opsiyonel)"
                  helperText="Sadece belirli mesajları filtrelemek için regex kullanın"
                />
              </TableCell>
            </TableRow>

            <TableRow>
              <TableCell>Veri Ayrıştırma</TableCell>
              <TableCell>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.socketTriggerSettings.parseData}
                      onChange={(e) => {
                        setSettings(prev => ({
                          ...prev,
                          socketTriggerSettings: {
                            ...prev.socketTriggerSettings,
                            parseData: e.target.checked
                          }
                        }));
                      }}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: '#edbc45',
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: '#edbc45',
                        },
                      }}
                    />
                  }
                  label="Gelen veriyi görev parametrelerine dönüştür"
                />
              </TableCell>
            </TableRow>
          </>
        )}
      </>
    );

    const renderWebSocketTriggerSettings = () => (
      <>
        {settings.executionMode === 'websocket-trigger' && (
          <>
            <TableRow>
              <TableCell colSpan={2}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: '#6a1b9a',
                    fontWeight: 600,
                    mt: 2
                  }}
                >
                  WebSocket Tetikleyici Ayarları
                </Typography>
              </TableCell>
            </TableRow>

            <TableRow>
              <TableCell>WebSocket Bağlantısı</TableCell>
              <TableCell>
                <FormControl fullWidth size="small">
                  <Select
                    value={settings.webSocketTriggerSettings.webSocketId}
                    onChange={handleChange('webSocketTriggerSettings', 'webSocketId')}
                    displayEmpty
                  >
                    <MenuItem value={0}>WebSocket seçin...</MenuItem>
                    {(webSockets || []).map(ws => (
                      <MenuItem key={ws.webSocketId} value={ws.webSocketId}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <WifiIcon size={16} />
                          {ws.connectionName} ({ws.webSocketUrl})
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </TableCell>
            </TableRow>

            <TableRow>
              <TableCell>Tetikleme Olayı</TableCell>
              <TableCell>
                <FormControl fullWidth size="small">
                  <Select
                    value={settings.webSocketTriggerSettings.triggerEvent}
                    onChange={handleChange('webSocketTriggerSettings', 'triggerEvent')}
                  >
                    <MenuItem value="message_received">Mesaj Alındı</MenuItem>
                    <MenuItem value="message_sent">Mesaj Gönderildi</MenuItem>
                    <MenuItem value="connection_status">Bağlantı Durumu Değişti</MenuItem>
                  </Select>
                </FormControl>
              </TableCell>
            </TableRow>

            <TableRow>
              <TableCell>Mesaj Tipi</TableCell>
              <TableCell>
                <TextField
                  fullWidth
                  size="small"
                  value={settings.webSocketTriggerSettings.messageType}
                  onChange={handleChange('webSocketTriggerSettings', 'messageType')}
                  placeholder="Mesaj tipi filtresi (opsiyonel)"
                  helperText="Belirli mesaj tiplerini filtrelemek için"
                />
              </TableCell>
            </TableRow>

            <TableRow>
              <TableCell>Mesaj Filtresi</TableCell>
              <TableCell>
                <TextField
                  fullWidth
                  size="small"
                  value={settings.webSocketTriggerSettings.messageFilter}
                  onChange={handleChange('webSocketTriggerSettings', 'messageFilter')}
                  placeholder="JSON path veya regex (opsiyonel)"
                  helperText="Mesaj içeriğini filtrelemek için JSON path veya regex kullanın"
                />
              </TableCell>
            </TableRow>

            <TableRow>
              <TableCell>Mesaj Ayrıştırma</TableCell>
              <TableCell>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.webSocketTriggerSettings.parseMessage}
                      onChange={(e) => {
                        setSettings(prev => ({
                          ...prev,
                          webSocketTriggerSettings: {
                            ...prev.webSocketTriggerSettings,
                            parseMessage: e.target.checked
                          }
                        }));
                      }}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: '#edbc45',
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: '#edbc45',
                        },
                      }}
                    />
                  }
                  label="Gelen mesajı görev parametrelerine dönüştür"
                />
              </TableCell>
            </TableRow>
          </>
        )}
      </>
    );

    const renderSchedulerSettings = () => (
      <>
        {settings.executionMode === 'scheduler' && (
          <>
            <TableRow>
              <TableCell colSpan={2}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: '#6a1b9a',
                    fontWeight: 600,
                    mt: 2
                  }}
                >
                  Zamanlama Ayarları
                </Typography>
              </TableCell>
            </TableRow>

            <TableRow>
              <TableCell>Zamanlama Tipi</TableCell>
              <TableCell>
                <FormControl fullWidth size="small">
                  <Select
                    value={settings.schedulerSettings.type}
                    onChange={handleChange('schedulerSettings', 'type')}
                  >
                    {schedulerTypes.map(type => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </TableCell>
            </TableRow>

            {settings.schedulerSettings.type === 'interval' && (
              <TableRow>
                <TableCell>Çalışma Aralığı</TableCell>
                <TableCell>
                  <FormControl fullWidth size="small">
                    <Select
                      value={settings.schedulerSettings.interval}
                      onChange={handleChange('schedulerSettings', 'interval')}
                    >
                      {intervalPresets.map(preset => (
                        <MenuItem key={preset.value} value={preset.value}>
                          {preset.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </TableCell>
              </TableRow>
            )}

            {settings.schedulerSettings.type === 'specific' && (
              <TableRow>
                <TableCell>Çalışma Saatleri</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {Array.isArray(settings.schedulerSettings.specificTimes) &&
                      settings.schedulerSettings.specificTimes.map((time, index) => (
                      <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <TextField
                          type="time"
                          size="small"
                          value={time}
                          onChange={(e) => handleTimeChange(index, e.target.value)}
                          sx={{ width: '150px' }}
                        />
                        {settings.schedulerSettings.specificTimes.length > 1 && (
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveTime(index)}
                            sx={{ color: 'error.main' }}
                          >
                            <DeleteOutlineIcon size={18} />
                          </IconButton>
                        )}
                      </Box>
                    ))}
                    <Button
                      startIcon={<AddIcon size={16} />}
                      onClick={handleAddTime}
                      size="small"
                      sx={{
                        alignSelf: 'flex-start',
                        mt: 1,
                        color: '#6a1b9a',
                        borderColor: '#6a1b9a',
                        '&:hover': {
                          borderColor: '#4a0e6a',
                          backgroundColor: 'rgba(106, 27, 154, 0.04)'
                        }
                      }}
                      variant="outlined"
                    >
                      Saat Ekle
                    </Button>
                  </Box>
                </TableCell>
              </TableRow>
            )}

            {/* CRON UI kaldırıldı - scheduler ile uyumsuzluk nedeniyle */}
          </>
        )}
      </>
    );

  const renderCodeSettings = () => (
    <>
      <TableRow>
        <TableCell colSpan={2}>
          <Typography
            variant="subtitle2"
            sx={{
              color: '#6a1b9a',
              fontWeight: 600
            }}
          >
            Kod Ayarları
          </Typography>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell>Programlama Dili</TableCell>
        <TableCell>
          <FormControl fullWidth size="small">
            <Select
              value={codeSettings.language}
              onChange={handleCodeSettingChange('language')}
            >
              {programmingLanguages.map(lang => (
                <MenuItem key={lang.value} value={lang.value}>{lang.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </TableCell>
      </TableRow>
    </>
  );

return (
  <>
  <Dialog
    open={open}
    onClose={handleClose}
    maxWidth="md"
    fullWidth
    PaperProps={{
      sx: {
        bgcolor: '#fefaf4',
        maxHeight: '90vh'
      }
    }}
  >
    <DialogTitle
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        bgcolor: '#6a1b9a',
        color: 'white',
        px: 2
      }}
    >
      <Box display="flex" alignItems="center" gap={1}>
        <SettingsIcon size={20} />
        <Typography>{isNew ? 'Yeni Görev Oluştur' : 'Görev Ayarları'}</Typography>
      </Box>
      <IconButton edge="end" color="inherit" onClick={handleClose}>
        <CloseIcon size={20} />
      </IconButton>
    </DialogTitle>

    <DialogContent sx={{ p: 2 }}>
      <TableContainer component={Paper} sx={{ backgroundColor: 'white' }}>
        <Table>
          <TableBody>
            {/* Temel Ayarlar */}
            <TableRow>
              <TableCell colSpan={2}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: '#6a1b9a',
                    fontWeight: 600
                  }}
                >
                  Temel Ayarlar
                </Typography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ width: '30%' }}>Görev Adı</TableCell>
              <TableCell sx={{ width: '70%' }}>
                <TextField
                  fullWidth
                  size="small"
                  value={settings.taskName}
                  onChange={handleChange('taskName')}
                  placeholder="Görev adı girin"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&.Mui-focused fieldset': {
                        borderColor: '#edbc45',
                      },
                    },
                  }}
                />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Görev Tipi</TableCell>
              <TableCell>
                <FormControl component="fieldset">
                  <RadioGroup
                    row
                    value={settings.taskType}
                    onChange={handleChange('taskType')}
                  >
                    <FormControlLabel
                      value="workflow"
                      control={
                        <Radio
                          sx={{
                            '&.Mui-checked': {
                              color: '#edbc45',
                            },
                          }}
                        />
                      }
                      label={<Box display="flex" alignItems="center" gap={1}>
                        <AccountTreeIcon size={16} /> Workflow
                      </Box>}
                    />
                    <FormControlLabel
                      value="code"
                      control={
                        <Radio
                          sx={{
                            '&.Mui-checked': {
                              color: '#edbc45',
                            },
                          }}
                        />
                      }
                      label={<Box display="flex" alignItems="center" gap={1}>
                        <CodeIcon size={16} /> Kod
                      </Box>}
                    />
                  </RadioGroup>
                </FormControl>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Çalışma Konumu</TableCell>
              <TableCell>
                <FormControl component="fieldset">
                  <RadioGroup
                    row
                    value={settings.executionLocation}
                    onChange={handleChange('executionLocation')}
                  >
                    <FormControlLabel
                      value="local"
                      control={
                        <Radio
                          sx={{
                            '&.Mui-checked': {
                              color: '#edbc45',
                            },
                          }}
                        />
                      }
                      label={<Box display="flex" alignItems="center" gap={1}>
                        <StorageIcon size={16} /> Lokal
                      </Box>}
                    />
                    <FormControlLabel
                      value="cloud"
                      control={
                        <Radio
                          sx={{
                            '&.Mui-checked': {
                              color: '#edbc45',
                            },
                          }}
                        />
                      }
                      label={<Box display="flex" alignItems="center" gap={1}>
                        <CloudQueueIcon size={16} /> Bulut
                      </Box>}
                    />
                  </RadioGroup>
                </FormControl>
              </TableCell>
            </TableRow>

            {/* If task type is 'code', show Code Settings */}
            {settings.taskType === 'code' && renderCodeSettings()}

            {/* Workflow/Code selection area */}
            {renderWorkflowCodeSelection()}

            {/* Execution Mode Settings */}
            <TableRow>
              <TableCell colSpan={2}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: '#6a1b9a',
                    fontWeight: 600,
                    mt: 2
                  }}
                >
                  Çalışma Modu
                </Typography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Çalışma Modu</TableCell>
              <TableCell>
                <FormControl fullWidth size="small">
                  <Select
                    value={settings.executionMode}
                    onChange={handleChange('executionMode')}
                    sx={{
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#edbc45',
                      },
                    }}
                  >
                    <MenuItem value="scheduler">
                      <Box display="flex" alignItems="center" gap={1}>
                        <AccessTimeIcon size={16} /> Zamanlayıcı
                      </Box>
                    </MenuItem>
                    <MenuItem value="tag-based">
                      <Box display="flex" alignItems="center" gap={1}>
                        <TagIcon size={16} /> Tag Tabanlı
                      </Box>
                    </MenuItem>
                    <MenuItem value="socket-trigger">
                      <Box display="flex" alignItems="center" gap={1}>
                        <StorageIcon size={16} /> Socket Tetikleyici
                      </Box>
                    </MenuItem>
                    <MenuItem value="websocket-trigger">
                      <Box display="flex" alignItems="center" gap={1}>
                        <CloudQueueIcon size={16} /> WebSocket Tetikleyici
                      </Box>
                    </MenuItem>
                    <MenuItem value="trigger">
                      <Box display="flex" alignItems="center" gap={1}>
                        <CodeIcon size={16} /> Tetikleyici
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>
              </TableCell>
            </TableRow>

            {/* Schedule Settings */}
            {renderSchedulerSettings()}
            {/* Tag-based Settings */}
            {renderTagBasedSettings()}
            {/* Socket Trigger Settings */}
            {renderSocketTriggerSettings()}
            {/* WebSocket Trigger Settings */}
            {renderWebSocketTriggerSettings()}
            {/* Other Settings */}
            <TableRow>
              <TableCell colSpan={2}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: '#6a1b9a',
                    fontWeight: 600,
                    mt: 2
                  }}
                >
                  Diğer Ayarlar
                </Typography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Timeout Süresi (saniye)</TableCell>
              <TableCell>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  value={settings.timeout}
                  onChange={handleChange('timeout')}
                  InputProps={{ inputProps: { min: 1 } }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&.Mui-focused fieldset': {
                        borderColor: '#edbc45',
                      },
                    },
                  }}
                />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Durum</TableCell>
              <TableCell>
              <Box sx={{ display: 'flex', gap: 4 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.isEnabled}
                      onChange={handleSwitchChange('isEnabled')}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: '#edbc45',
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: '#edbc45',
                        },
                      }}
                    />
                  }
                  label={settings.isEnabled ? "Aktif" : "Pasif"}
                />
                <FormControlLabel
                      control={
                        <Switch
                          checked={settings.enableLogging}
                          onChange={handleSwitchChange('enableLogging')}
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              color: '#edbc45',
                            },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                              backgroundColor: '#edbc45',
                            },
                          }}
                        />
                      }
                      label={settings.enableLogging ? "Loglama Aktif" : "Loglama Pasif"}
                    />
                  </Box>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </DialogContent>

    <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
      <Box>
        {!isNew && (
          <Button
            onClick={handleDelete}
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon size={16} />}
          >
            Görevi Sil
          </Button>
        )}
      </Box>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{
            borderColor: '#6a1b9a',
            color: '#6a1b9a',
            '&:hover': {
              borderColor: '#4a0e6a',
              backgroundColor: 'rgba(106, 27, 154, 0.04)'
            }
          }}
        >
          İptal
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          sx={{
            bgcolor: '#edbc45',
            color: '#000',
            '&:hover': {
              bgcolor: '#d4a635'
            }
          }}
        >
          {isNew ? 'Oluştur' : 'Kaydet'}
        </Button>
      </Box>
    </DialogActions>

  </Dialog>
  </>
);
}
export default TaskSettingsDialog;
